import { WORKFLOW_SCHEMA } from "constants/validation";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import set from "lodash/set";
import upperFirst from "lodash/upperFirst";
import { EVENT_NAME } from "utils/constants/event";
import { FORM_KEY } from "utils/constants/form";
import { STATE_KEYS } from "utils/constants/state";
import {
  WORKFLOW_SERVICE_ACTION_OPTIONS,
  WORKFLOW_SERVICE
} from "utils/constants/workflow";
import { getUUID } from "utils/uuid";
import { validateSchema } from "utils/validate";
import { TEMPLATE_EDITOR_NODE } from "utils/constants/variable";

export const scaffoldWorkflowStep = (integration) => {
  const service = integration.service;

  if (service === WORKFLOW_SERVICE.DOCUSIGN) {
    return {
      uuid: getUUID(),
      integrationId: integration.id,
      triggers: {
        [EVENT_NAME.MERCHANT_ACCOUNT_CHECKOUT_COMPLETED]: true
      },
      service,
      action: WORKFLOW_SERVICE_ACTION_OPTIONS[service][0].uid,
      config: {
        templateId: null,
        recipients: {
          signers: []
        }
      }
    };
  } else {
    throw new Error(`${service} not yet supported.`);
  }
};

export const prepareEditWorkflowInitialValues = ({ workflow, variables }) => {
  const focusStepUuid = get(workflow, `${STATE_KEYS.WORKFLOW.STEPS}[0].uuid`);
  return {
    [FORM_KEY]: {
      focusStepUuid: focusStepUuid || null,
      variables
    },
    ...workflow
  };
};

export const prepareUpdateWorkflow = (values) => {
  const { [FORM_KEY]: formData, ...workflow } = values;
  if (!workflow.title) {
    workflow.title = "Workflow";
  }
  if (!workflow.description) {
    delete workflow.description;
  }
  return workflow;
};

export const scaffoldWorkflow = ({ title }) => {
  const titleParts = ["workflow"];
  if (title) {
    titleParts.unshift(title);
  }
  return {
    title: upperFirst(titleParts.join(" ")),
    uuid: getUUID(),
    data: {
      steps: []
    }
  };
};

const reduceStructuredContent = ({ content }) => {
  return Array.isArray(content.content)
    ? content.content
        .reduce((memo, node) => {
          if (
            node.type === TEMPLATE_EDITOR_NODE.PARAGRAPH &&
            Array.isArray(node.content) &&
            node.content.length
          ) {
            for (let nodeIx = 0; nodeIx < node.content.length; nodeIx++) {
              const subNode = node.content[nodeIx];

              if (subNode.type === TEMPLATE_EDITOR_NODE.MENTION) {
                if (subNode.attrs.label) {
                  memo += subNode.attrs.label;
                }
              } else if (subNode.type === TEMPLATE_EDITOR_NODE.TEXT) {
                if (subNode.text) {
                  memo += subNode.text;
                }
              }
            }
          }

          return memo;
        }, "")
        .trim()
    : "";
};

export const validateWorfklowSchema = async (values) => {
  const validResult = await validateSchema(WORKFLOW_SCHEMA, values);
  const steps = values.data.steps;
  for (let stepIx = 0; stepIx < steps.length; stepIx++) {
    const step = steps[stepIx];
    const stepConfigName = `data.steps[${stepIx}].config`;

    if (!step.action) {
      set(validResult, `${stepConfigName}.action`, "Action is required");
    }
    if (!step.integrationId) {
      set(
        validResult,
        `${stepConfigName}.integrationId`,
        "Integration is required"
      );
    }
    if (step.service === "docusign") {
      if (!step.config.templateId) {
        set(
          validResult,
          `${stepConfigName}.templateId`,
          "Template is required"
        );
      }
      if (!step.action) {
        set(validResult, `${stepConfigName}.action`, "Action is required");
      }

      if (isEmpty(step.config.recipients)) {
        set(
          validResult,
          `${stepConfigName}.recipients`,
          "Recipients are required"
        );
      } else {
        if (isEmpty(step.config.recipients.signers)) {
          set(
            validResult,
            `${stepConfigName}.recipients.signers`,
            "Minimum of one signer is required"
          );
        } else {
          const signers = step.config.recipients.signers;
          for (let signerIx = 0; signerIx < signers.length; signerIx++) {
            const signerKey = `${stepConfigName}.recipients.signers[${signerIx}]`;
            const signer = signers[signerIx];

            if (
              !signer.email.value ||
              !reduceStructuredContent({
                content: signer.email.value
              }).trim()
            ) {
              set(validResult, `${signerKey}.email.value`, "Email is required");
            }
          }
        }
      }
    }
  }

  return validResult;
};
